import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Popup from "../../components/Popup/Popup";
import { updateEmployee, clearUpdateEmployeeFeedback } from "../../redux/authSlice";
import "./EditProfilePopup.css";

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];

const SOCIAL_FIELDS = [
    { key: "linkedin", label: "LinkedIn" },
    { key: "github", label: "GitHub" },
    { key: "gitlab", label: "GitLab" },
    { key: "instagram", label: "Instagram" },
    { key: "x", label: "X (Twitter)" },
    { key: "facebook", label: "Facebook" },
    { key: "tiktok", label: "TikTok" },
    { key: "artstation", label: "ArtStation" },
];

// Campos que la API no deja editar en el propio perfil (solo RRHH): se muestran deshabilitados.
const HR_ONLY_HINT = "Solo RRHH puede modificar este campo";

// Solo campos de UpdateMyProfileDto (PATCH /employees/me rechaza cualquier campo extra).
const buildProfilePayload = (form) => {
    const payload = {
        nickname: form.nickname.trim(),
        description: form.description.trim(),
        eps: form.eps.trim(),
        metamaskWallet: form.metamaskWallet.trim(),
        size: {
            sweatshirt: form.size.sweatshirt,
            tshirt: form.size.tshirt,
        },
        socialNetworks: Object.fromEntries(
            Object.entries(form.socialNetworks).map(([key, value]) => [key, (value || "").trim()])
        ),
        bankAccounts: form.bankAccounts
            .map((account) => ({
                bank: (account.bank || "").trim(),
                bankAccountNumber: (account.bankAccountNumber || "").trim(),
            }))
            .filter((account) => account.bank || account.bankAccountNumber),
    };

    if (form.birthdayDate) payload.birthdayDate = form.birthdayDate;
    if (form.binanceEmail.trim()) payload.binanceEmail = form.binanceEmail.trim();

    return payload;
};

const buildInitialForm = (employee) => ({
    name: employee?.name || "",
    nickname: employee?.nickname || "",
    role: employee?.role || "",
    documentId: employee?.documentId || "",
    birthdayDate: employee?.birthdayDate
        ? (String(employee.birthdayDate).match(/^(\d{4}-\d{2}-\d{2})/) || [null, ""])[1]
        : "",
    eps: employee?.eps || "",
    description: employee?.description || "",
    binanceEmail: employee?.binanceEmail || "",
    metamaskWallet: employee?.metamaskWallet || "",
    discordId: employee?.discordId || "",
    size: {
        sweatshirt: employee?.size?.sweatshirt || "",
        tshirt: employee?.size?.tshirt || "",
    },
    socialNetworks: {
        x: employee?.socialNetworks?.x || "",
        instagram: employee?.socialNetworks?.instagram || "",
        facebook: employee?.socialNetworks?.facebook || "",
        tiktok: employee?.socialNetworks?.tiktok || "",
        linkedin: employee?.socialNetworks?.linkedin || "",
        artstation: employee?.socialNetworks?.artstation || "",
        github: employee?.socialNetworks?.github || "",
        gitlab: employee?.socialNetworks?.gitlab || "",
    },
    bankAccounts: employee?.bankAccounts?.map((b) => ({
        bank: b?.bank || "",
        bankAccountNumber: b?.bankAccountNumber || "",
    })) || [],
});

export default function EditProfilePopup({ isOpen, onClose }) {
    const dispatch = useDispatch();
    const { employee, statusUpdateEmployee, errorUpdateEmployee } = useSelector(
        (state) => state.auth
    );

    const [form, setForm] = useState(() => buildInitialForm(employee));
    const [openSection, setOpenSection] = useState("personal");

    useEffect(() => {
        if (!isOpen) return;
        dispatch(clearUpdateEmployeeFeedback());
        setForm(buildInitialForm(employee));
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (statusUpdateEmployee === "succeeded") {
            handleClose();
        }
    }, [statusUpdateEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        dispatch(clearUpdateEmployeeFeedback());
        onClose();
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSizeChange = (field, value) => {
        setForm((prev) => ({ ...prev, size: { ...prev.size, [field]: value } }));
    };

    const handleSocialChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            socialNetworks: { ...prev.socialNetworks, [field]: value },
        }));
    };

    const handleBankChange = (index, field, value) => {
        setForm((prev) => {
            const accounts = [...prev.bankAccounts];
            accounts[index] = { ...accounts[index], [field]: value };
            return { ...prev, bankAccounts: accounts };
        });
    };

    const addBank = () => {
        setForm((prev) => ({
            ...prev,
            bankAccounts: [...prev.bankAccounts, { bank: "", bankAccountNumber: "" }],
        }));
    };

    const removeBank = (index) => {
        setForm((prev) => ({
            ...prev,
            bankAccounts: prev.bankAccounts.filter((_, i) => i !== index),
        }));
    };

    const handleConfirm = () => {
        dispatch(updateEmployee(buildProfilePayload(form)));
    };

    const isPending = statusUpdateEmployee === "loading";

    const toggleSection = (sectionId) => {
        setOpenSection(openSection === sectionId ? null : sectionId);
    };

    const renderSection = (sectionId, title, children) => (
        <div className="edit-profile-section edit-profile-accordion-item">
            <button
                type="button"
                className={`edit-profile-accordion-header ${openSection === sectionId ? "open" : ""}`}
                onClick={() => toggleSection(sectionId)}
            >
                <h3 className="edit-profile-section-title">{title}</h3>
                <span className="edit-profile-accordion-icon">▼</span>
            </button>
            {openSection === sectionId && (
                <div className="edit-profile-accordion-body">
                    {children}
                </div>
            )}
        </div>
    );

    return (
        <Popup
            isOpen={isOpen}
            title="Editar Perfil"
            onClose={handleClose}
            onConfirm={handleConfirm}
            confirmText={isPending ? "Guardando..." : "Guardar cambios"}
            confirmDisabled={isPending}
            cancelDisabled={isPending}
            maxWidth="700px"
            height="88vh"
        >
            <div className="edit-profile-form">
                {/* Personal */}
                {renderSection(
                    "personal",
                    "Información Personal",
                    <div className="edit-profile-grid">
                        <div className="edit-profile-field edit-profile-field--full">
                            <label htmlFor="ep-name">Nombre completo</label>
                            <input
                                id="ep-name"
                                type="text"
                                disabled
                                title={HR_ONLY_HINT}
                                value={form.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-nickname">Nickname</label>
                            <input
                                id="ep-nickname"
                                type="text"
                                value={form.nickname}
                                onChange={(e) => handleChange("nickname", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-role">Rol</label>
                            <input
                                id="ep-role"
                                type="text"
                                disabled
                                title={HR_ONLY_HINT}
                                value={form.role}
                                onChange={(e) => handleChange("role", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-docid">Número de documento</label>
                            <input
                                id="ep-docid"
                                type="text"
                                disabled
                                title={HR_ONLY_HINT}
                                value={form.documentId}
                                onChange={(e) => handleChange("documentId", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-birthday-date">Fecha de cumpleaños</label>
                            <input
                                id="ep-birthday-date"
                                type="date"
                                value={form.birthdayDate}
                                onChange={(e) => handleChange("birthdayDate", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-eps">EPS</label>
                            <input
                                id="ep-eps"
                                type="text"
                                value={form.eps}
                                onChange={(e) => handleChange("eps", e.target.value)}
                            />
                        </div>
                        <div className="edit-profile-field edit-profile-field--full">
                            <label htmlFor="ep-description">
                                Descripción
                                <span className="edit-profile-field-note">
                                    Esta descripción se usará para generar el certificado laboral
                                </span>
                            </label>
                            <textarea
                                id="ep-description"
                                value={form.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                rows={2}
                                placeholder="Describe brevemente tu cargo y funciones..."
                            />
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-tshirt">Camiseta</label>
                            <select
                                id="ep-tshirt"
                                value={form.size.tshirt}
                                onChange={(e) => handleSizeChange("tshirt", e.target.value)}
                            >
                                <option value="">Sin especificar</option>
                                {SIZE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="edit-profile-field">
                            <label htmlFor="ep-sweatshirt">Sudadera</label>
                            <select
                                id="ep-sweatshirt"
                                value={form.size.sweatshirt}
                                onChange={(e) => handleSizeChange("sweatshirt", e.target.value)}
                            >
                                <option value="">Sin especificar</option>
                                {SIZE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Payment methods */}
                {renderSection(
                    "payment",
                    "Formas de Pago",
                    <>
                        <div className="edit-profile-grid">
                            <div className="edit-profile-field edit-profile-field--full">
                                <label htmlFor="ep-binance">Email Binance</label>
                                <input
                                    id="ep-binance"
                                    type="email"
                                    value={form.binanceEmail}
                                    onChange={(e) => handleChange("binanceEmail", e.target.value)}
                                />
                            </div>
                            <div className="edit-profile-field edit-profile-field--full">
                                <label htmlFor="ep-metamask">Wallet Metamask</label>
                                <input
                                    id="ep-metamask"
                                    type="text"
                                    value={form.metamaskWallet}
                                    onChange={(e) => handleChange("metamaskWallet", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="edit-profile-banks">
                            <div className="edit-profile-banks-label">Cuentas bancarias</div>
                            {form.bankAccounts.map((account, i) => (
                                <div key={i} className="edit-profile-bank-row">
                                    <div className="edit-profile-field">
                                        <label>Banco</label>
                                        <input
                                            type="text"
                                            value={account.bank}
                                            onChange={(e) =>
                                                handleBankChange(i, "bank", e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="edit-profile-field">
                                        <label>Número de cuenta</label>
                                        <input
                                            type="text"
                                            value={account.bankAccountNumber}
                                            onChange={(e) =>
                                                handleBankChange(i, "bankAccountNumber", e.target.value)
                                            }
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="edit-profile-remove-btn"
                                        onClick={() => removeBank(i)}
                                        aria-label="Eliminar cuenta bancaria"
                                    >
                                        –
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="edit-profile-add-btn"
                                onClick={addBank}
                            >
                                + Agregar cuenta bancaria
                            </button>
                        </div>
                    </>
                )}

                {/* Social Networks */}
                {renderSection(
                    "social",
                    "Redes Sociales",
                    <div className="edit-profile-grid">
                        <div className="edit-profile-field">
                            <label htmlFor="ep-discord">Discord ID</label>
                            <input
                                id="ep-discord"
                                type="text"
                                disabled
                                title={HR_ONLY_HINT}
                                value={form.discordId}
                                onChange={(e) => handleChange("discordId", e.target.value)}
                            />
                        </div>
                        {SOCIAL_FIELDS.map(({ key, label }) => (
                            <div key={key} className="edit-profile-field">
                                <label htmlFor={`ep-social-${key}`}>{label}</label>
                                <input
                                    id={`ep-social-${key}`}
                                    type="url"
                                    value={form.socialNetworks[key]}
                                    onChange={(e) => handleSocialChange(key, e.target.value)}
                                    placeholder="https://"
                                />
                            </div>
                        ))}
                    </div>
                )}

                {errorUpdateEmployee && (
                    <p className="edit-profile-error">{errorUpdateEmployee}</p>
                )}
            </div>
        </Popup>
    );
}
